export type DeckId = "reflection" | "surprise" | "grounding" | "mystery";

export type DeckCard = {
  text: string;
  /** Optional extra detail or steps. */
  steps?: string[];
  /** Tokens gifted when the card is accepted/completed (never taken away). */
  reward?: number;
  /** Mystery effects. */
  effect?: { move?: number; rollAgain?: boolean };
  link?: { to: string; label: string };
};

export const DECKS: Record<DeckId, { name: string; emoji: string; motif: string; tone: string; blurb: string; cards: DeckCard[] }> = {
  reflection: {
    name: "Reflection Card", emoji: "🪞", motif: "🌙✨🌿", tone: "rt-deck-reflection",
    blurb: "A gentle question. Answer, think quietly, or skip — all are welcome.",
    cards: [
      { text: "What is one feeling you noticed today, without needing to change it?", link: { to: "/how-are-you-feeling", label: "Explore feelings" } },
      { text: "When do you feel most like yourself?", link: { to: "/journal", label: "Open Journal" } },
      { text: "What is one small sign that your body feels safe?", link: { to: "/somatic", label: "Somatic" } },
      { text: "Which word would you like to carry with you today?", link: { to: "/affirmations", label: "Affirmations" } },
      { text: "What is one 'no' you are proud of saying — or would like to practise?", link: { to: "/journal", label: "Open Journal" } },
      { text: "What did play or fun look like for you as a child?", link: { to: "/inner-child", label: "Inner Child" } },
      { text: "What helps you come back to the present when old feelings arrive?", link: { to: "/grounding", label: "Grounding" } },
      { text: "Who taught you that your needs matter — or who could you learn it from now?", link: { to: "/journal", label: "Open Journal" } },
      { text: "What is one way you have changed or grown in the last year?", link: { to: "/journals", label: "Journals" } },
      { text: "What does rest feel like for you, and what gets in its way?", link: { to: "/sounds", label: "Calming Sounds" } },
      { text: "Which relationship in your life feels calm and steady?", link: { to: "/journal", label: "Open Journal" } },
      { text: "What would you like to forgive yourself for — only if you are ready?", link: { to: "/self-love", label: "Self-Love" } },
      { text: "What is a place, real or imagined, where you feel peaceful?", link: { to: "/meditations", label: "Meditations" } },
      { text: "What did the younger you need to hear more often?", link: { to: "/inner-child", label: "Inner Child" } },
      { text: "Where in your life would a small, kind boundary help you feel safer?", link: { to: "/journal", label: "Open Journal" } },
      { text: "What is one thing you've survived that you rarely give yourself credit for?", link: { to: "/self-love", label: "Self-Love" } },
      { text: "Who or what helps you feel even a little bit safe?", link: { to: "/journals", label: "Journals" } },
      { text: "What does 'healing' look like for you this week — even in a tiny way?", link: { to: "/journal", label: "Open Journal" } },
      { text: "Which survival response (fight, flight, freeze, fawn) feels most familiar? No judgement — just noticing.", link: { to: "/how-are-you-feeling", label: "Explore feelings" } },
      { text: "What is something kind you could say to yourself right now?", link: { to: "/affirmations", label: "Affirmations" } },
    ],
  },
  surprise: {
    name: "Healing Surprise", emoji: "🎁", motif: "🌸🌻🦋", tone: "rt-deck-surprise",
    blurb: "A little gift for showing up.",
    cards: [
      { text: "A butterfly lands nearby. You showed up for yourself today.", reward: 2 },
      { text: "Sunshine through the trees! Rest is part of healing.", reward: 2, link: { to: "/sounds", label: "Calming Sounds" } },
      { text: "You found a smooth river stone — a reminder that you are steady.", reward: 3 },
      { text: "A kind letter arrives: 'You are doing better than you think.'", reward: 2, link: { to: "/affirmations", label: "Affirmations" } },
      { text: "Wildflowers bloom along your path. Growth happens quietly.", reward: 3 },
      { text: "A warm cup of tea appears. Take a sip-sized pause.", reward: 1 },
      { text: "A rainbow appears after the rain. Hard days pass.", reward: 2 },
      { text: "You find a cosy blanket on a bench. Wrap yourself in a moment of comfort.", reward: 1, link: { to: "/sounds", label: "Calming Sounds" } },
      { text: "A robin sings just for you. Your voice matters too.", reward: 2 },
      { text: "You spot a four-leaf clover. Small good things are still out there.", reward: 3 },
      { text: "A friend waves from across the meadow. You are not alone on this path.", reward: 2, link: { to: "/community", label: "Community" } },
      { text: "The moon lights your way. Even slow steps move you forward.", reward: 2 },
      { text: "You find a little seed in your pocket. Plant a kind intention for tomorrow.", reward: 1, link: { to: "/journal", label: "Open Journal" } },
      { text: "Warm sun on your face. Let your shoulders soften for a moment.", reward: 2 },
      { text: "A gentle stream sings nearby. Breathe with its rhythm.", reward: 2, link: { to: "/breathing", label: "Breathing" } },
      { text: "You notice how far you've come along the path. Well done.", reward: 3 },
    ],
  },
  grounding: {
    name: "Grounding Challenge", emoji: "🪨", motif: "🌊🍃🪵", tone: "rt-deck-grounding",
    blurb: "A short practice for your nervous system. Try it, or skip it.",
    cards: [
      { text: "Breathe out longer than you breathe in.", steps: ["In for 4", "Out for 6", "Repeat 3 times"], reward: 1, link: { to: "/breathing", label: "Breathing" } },
      { text: "5-4-3-2-1 senses.", steps: ["5 things you see", "4 you can touch", "3 you hear", "2 you smell", "1 you taste"], reward: 1, link: { to: "/grounding", label: "Grounding" } },
      { text: "Feel your feet.", steps: ["Press both feet into the floor", "Notice the support beneath you", "Say: 'I am here, now'"], reward: 1 },
      { text: "Butterfly hug.", steps: ["Cross your arms over your chest", "Tap shoulders slowly, left then right", "Continue for 30 seconds"], reward: 1, link: { to: "/tapping", label: "Tapping" } },
      { text: "Shake it out.", steps: ["Shake your hands for 10 seconds", "Roll your shoulders", "Notice how your body feels"], reward: 1, link: { to: "/somatic", label: "Somatic" } },
      { text: "Find something blue.", steps: ["Look around for 3 blue things", "Describe one in detail to yourself"], reward: 1 },
    ],
  },
  mystery: {
    name: "Mystery Card", emoji: "🔮", motif: "⭐🍄🌀", tone: "rt-deck-mystery",
    blurb: "Something unexpected is waiting…",
    cards: [
      { text: "A gentle breeze carries you forward 2 spaces.", effect: { move: 2 } },
      { text: "You discover a hidden path — move forward 1 space.", effect: { move: 1 } },
      { text: "A friendly fox offers you another turn. Roll again!", effect: { rollAgain: true } },
      { text: "You discover a secret garden with 3 bonus tokens.", reward: 3 },
      { text: "A shooting star! Move forward 3 spaces.", effect: { move: 3 } },
      { text: "A quiet clearing invites you to stay. Enjoy the stillness and collect 1 token.", reward: 1 },
    ],
  },
};

/** Board spaces that draw a card when landed on (never corners or Support & Safety). */
export const DRAW_SPACES: Record<number, DeckId> = {
  2: "reflection", 4: "surprise", 8: "grounding", 10: "mystery",
  14: "reflection", 16: "surprise", 20: "grounding", 22: "mystery",
};

export const LEVELS = [
  { at: 0, name: "Seedling", emoji: "🌱" },
  { at: 6, name: "Sprout", emoji: "🌿" },
  { at: 15, name: "Bloom", emoji: "🌸" },
  { at: 30, name: "Blossoming Tree", emoji: "🌳" },
  { at: 50, name: "Healing Forest", emoji: "🏞️" },
];

export function levelFor(tokens: number) {
  let i = 0;
  LEVELS.forEach((l, idx) => { if (tokens >= l.at) i = idx; });
  const next = LEVELS[i + 1];
  const pct = next ? Math.round(((tokens - LEVELS[i].at) / (next.at - LEVELS[i].at)) * 100) : 100;
  return { level: LEVELS[i], next, pct };
}
